import { useState } from 'react';

import {
  FullScreenLoading,
  BackrsFormTextField,
  SIDEBAR_COLLAPSED_WIDTH,
} from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { selectUser } from '@backrs_ui/grow-ui/src/redux/user/user';

import { Box, SxProps, Theme } from '@mui/material';
import { useForm, Controller, FieldValues } from 'react-hook-form';

import { FieldRow } from './atoms/FieldRow';
import { SectionHeader } from './atoms/SectionHeader';
import { SECTIONS_KEYS, SETTINGS_FIELDS } from './config/settings.config';
import { useSettingsFormSubmit } from './hooks/useSettingsOnSubmit';
import { SettingsHeader } from './molecules/SettingsHeader';

const SETTINGS_STRINGS = appStrings.modules.settings;

// Helper function to convert MM/DD/YYYY to YYYY-MM-DD for HTML date input
const formatBirthdayForDateInput = (birthday: string): string => {
  if (!birthday) return '';

  // If already in YYYY-MM-DD format, return as is
  if (/^\d{4}-\d{2}-\d{2}$/.test(birthday)) {
    return birthday;
  }

  // Convert from MM/DD/YYYY to YYYY-MM-DD
  const parts = birthday.split('/');

  if (parts.length === 3) {
    const [month, day, year] = parts;

    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }

  return birthday;
};

export const SettingsPage = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [formSaveError, setFormSaveError] = useState<string | null>(null);

  const user = useAppSelector(selectUser);

  const defaultValues: FieldValues = {
    firstName: user?.first_name ?? '',
    lastName: user?.last_name ?? '',
    email: user?.email ?? '',
    phone: user?.phone ?? '',
    zipCode: user?.zip_code ?? '',
    birthday: user?.birthday ?? '',
    newPassword: '',
    confirmPassword: '',
  };

  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { isSubmitting },
  } = useForm<FieldValues>({
    defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  const submitHandler = useSettingsFormSubmit(user, setError, setFormSaveError);
  const onSubmit = handleSubmit(submitHandler);

  if (!user) {
    return <FullScreenLoading />;
  }

  return (
    <Box sx={pageContainerSx}>
      <Box sx={containerSx}>
        {/* HEADER */}
        <SettingsHeader
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          reset={reset}
          onSubmit={onSubmit}
          user={user}
          isLoading={isSubmitting}
          formSaveError={formSaveError}
          setFormSaveError={setFormSaveError}
        />

        {/* FORM */}
        <form onSubmit={onSubmit}>
          <Box sx={formSectionContainerSx}>
            {/* FIRST/LAST NAME */}
            <FieldRow label={SETTINGS_STRINGS.sections.name.title}>
              {SECTIONS_KEYS.NAME.map((key) => (
                <Controller
                  key={key}
                  name={key}
                  control={control}
                  rules={SETTINGS_FIELDS[key].rules}
                  render={({ field: controllerField, fieldState: { error } }) => (
                    <BackrsFormTextField
                      field={{ ...SETTINGS_FIELDS[key], label: null }}
                      groupedFields={{ name: SECTIONS_KEYS.NAME.map((k) => SETTINGS_FIELDS[k]) }}
                      index={0} // the index is only used for margin styling, which we don't want here
                      controllerField={controllerField}
                      error={error?.message}
                    />
                  )}
                />
              ))}
            </FieldRow>

            {/* OTHER FIELDS */}
            {SECTIONS_KEYS.OTHER.map((key, index) => (
              <Controller
                key={key}
                name={key}
                control={control}
                rules={SETTINGS_FIELDS[key].rules}
                render={({ field: controllerField, fieldState: { error } }) => {
                  const formattedValue =
                    key === 'birthday'
                      ? formatBirthdayForDateInput(controllerField.value ?? '')
                      : controllerField.value;

                  return (
                    <FieldRow
                      label={SETTINGS_FIELDS[key].label}
                      hideDivider={SETTINGS_FIELDS[key].hideDivider}
                    >
                      <BackrsFormTextField
                        field={{
                          ...SETTINGS_FIELDS[key],
                          label: null,
                        }}
                        groupedFields={{ [key]: [SETTINGS_FIELDS[key]] }}
                        index={index}
                        controllerField={{
                          ...controllerField,
                          value: formattedValue,
                        }}
                        error={error?.message}
                      />
                    </FieldRow>
                  );
                }}
              />
            ))}
          </Box>

          {/* PASSWORD/CONFIRM PASSWORD */}
          <SectionHeader
            title={SETTINGS_STRINGS.sections.password.title}
            description={SETTINGS_STRINGS.sections.password.description}
          />
          <Box sx={formSectionContainerSx}>
            {SECTIONS_KEYS.PASSWORD.map((key, index) => (
              <Controller
                key={key}
                name={key}
                control={control}
                rules={SETTINGS_FIELDS[key].rules}
                render={({ field: controllerField, fieldState: { error } }) => (
                  <FieldRow
                    label={SETTINGS_FIELDS[key].label}
                    hideDivider={SETTINGS_FIELDS[key].hideDivider}
                  >
                    <BackrsFormTextField
                      field={{ ...SETTINGS_FIELDS[key], label: null }}
                      groupedFields={{ [key]: [SETTINGS_FIELDS[key]] }}
                      index={index}
                      controllerField={controllerField}
                      error={error?.message}
                    />
                  </FieldRow>
                )}
              />
            ))}
          </Box>
        </form>
      </Box>
    </Box>
  );
};

const pageContainerSx: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  backgroundColor: backrs.colors['Colors/Base/White'],
  paddingTop: backrs.spacing['spacing-4xl'],
  paddingBottom: backrs.spacing['spacing-6xl'],

  paddingRight: backrs.spacing['spacing-none'],
  paddingLeft: backrs.spacing['spacing-none'],

  '@media (min-width: 901px) and (max-width: 1366px) and (max-height: 768px)': {
    maxWidth: '100%',
    paddingLeft: `${SIDEBAR_COLLAPSED_WIDTH}px`,
  },
  '@media (max-width: 900px) and (max-height: 504px)': {
    maxWidth: '100%',
    paddingLeft: '0px',
  },
  '@media (max-width: 405px)': {
    paddingLeft: '0px',
  },
});

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  paddingX: {
    xs: backrs.spacing['spacing-2xl'],
    sm: backrs.spacing['spacing-4xl'],
  },
  margin: '0 auto',
  overflowY: 'auto',
  maxWidth: '1144px !important',
  width: '100%',
  backgroundColor: backrs.colors['Colors/Base/White'],

  mb: { xs: backrs.spacing['spacing-8xl'], sm: backrs.spacing['spacing-none'] },
});

const formSectionContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-2xl'],
});
